import { apiFetchWithRetry } from './apiFetch';

// Mirrors AchievementType in Hypebook-API/prisma/schema.prisma. Kept as
// a string union (no Prisma import) so the Platform-Admin bundle doesn't
// have to pull backend types just for one shape.
export type AchievementType =
  | 'AWARD'
  | 'CERTIFICATION'
  | 'COURSE'
  | 'PROJECT'
  | 'JOB'
  // SPORT retired (Phase 2) — domain moved to the "Sports" Category. Legacy rows
  // backfilled to OTHER; value still exists in the DB enum but is never produced.
  | 'VOLUNTEER'
  | 'LEADERSHIP'
  | 'OTHER';

// Sortable columns for the admin achievements table. Mirrors
// ADMIN_ACHIEVEMENT_SORT_KEYS in the backend dashboard.service. The
// `verified` column is deliberately excluded (derived, not sortable).
export type AdminAchievementSortBy =
  | 'student'
  | 'school'
  | 'type'
  | 'title'
  | 'organization'
  | 'linkedAward'
  | 'createdAt';

export interface AdminAchievementListItem {
  id: string;
  title: string;
  achievementType: AchievementType;
  organization: string;
  categoryName: string | null;
  startDate: string;
  endDate: string | null;
  createdAt: string;
  verified: boolean;
  studentId: string;
  studentFirstName: string | null;
  studentLastName: string | null;
  studentEmail: string;
  schoolId: string | null;
  schoolName: string | null;
  // Linked-to-Award-catalog. awardId is the FK; linkedAward is a lite
  // snapshot (name, sponsor) for the admin column. Null when unlinked.
  awardId: string | null;
  linkedAward: { id: string; name: string; sponsor: string | null } | null;
}

export interface PaginationMeta {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AdminAchievementListResponse {
  data: AdminAchievementListItem[];
  meta: PaginationMeta;
}

export interface ListAdminAchievementsParams {
  type?: AchievementType;
  /** Substring, case-insensitive. Server-side ILIKE on organization. */
  organization?: string;
  schoolId?: string;
  /** Free-text search across title + organization. */
  q?: string;
  /** ISO date — createdAt >= startDate. */
  startDate?: string;
  /** ISO date — createdAt <= endDate. */
  endDate?: string;
  /** Filter by Award-catalog link. Omit for all. */
  linked?: 'linked' | 'unlinked';
  /**
   * Column to sort by, server-side (applies across all data, not just
   * the current page). The `verified` column is not sortable — it's
   * derived from a separate query. Defaults to `createdAt`.
   */
  sortBy?: AdminAchievementSortBy;
  /** Sort direction. Defaults to `desc`. */
  sortDir?: 'asc' | 'desc';
  page?: number;
  /** Clamped server-side to 1..100, default 25. */
  pageSize?: number;
}

// ─── Organizations rollup (Platform-Admin Organizations page) ────────────────

export type AdminOrganizationSortBy = 'organization' | 'achievementCount' | 'studentCount' | 'verifiedCount';

export interface AdminOrganizationItem {
  organization: string;
  achievementCount: number;
  studentCount: number;
  verifiedCount: number;
}

export interface AdminOrganizationListResponse {
  data: AdminOrganizationItem[];
  meta: PaginationMeta;
}

export interface ListAdminOrganizationsParams {
  q?: string;
  sortBy?: AdminOrganizationSortBy;
  sortDir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

// ─── Organization drill-downs (click a count in the Organizations table) ─────

export interface OrganizationStudentItem {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  schoolName: string | null;
  /** This student's achievement count *within this organization*. */
  achievementCount: number;
}

export interface OrganizationStudentListResponse {
  data: OrganizationStudentItem[];
  meta: PaginationMeta;
}

export const achievementService = {
  /**
   * Platform-Admin browse for achievements. Backend gates on
   * UserRole.ADMIN (the dashboard service's requireAdmin guard). All
   * filters are server-side so the page stays cheap at any scale.
   */
  listAdminAchievements: async (
    params: ListAdminAchievementsParams = {},
  ): Promise<AdminAchievementListResponse> => {
    const qs = new URLSearchParams();
    if (params.type) qs.append('type', params.type);
    if (params.organization) qs.append('organization', params.organization);
    if (params.schoolId) qs.append('schoolId', params.schoolId);
    if (params.q) qs.append('q', params.q);
    if (params.startDate) qs.append('startDate', params.startDate);
    if (params.endDate) qs.append('endDate', params.endDate);
    if (params.linked) qs.append('linked', params.linked);
    if (params.sortBy) qs.append('sortBy', params.sortBy);
    if (params.sortDir) qs.append('sortDir', params.sortDir);
    if (params.page) qs.append('page', String(params.page));
    if (params.pageSize) qs.append('pageSize', String(params.pageSize));

    const response = await apiFetchWithRetry(
      `/v1/dashboard/admins/achievements?${qs.toString()}`,
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.message || 'Failed to load achievements');
    }
    return data as AdminAchievementListResponse;
  },

  /**
   * Per-organization achievement rollup. Same backend admin gate as
   * listAdminAchievements; all filtering/sorting/paging is server-side.
   */
  listAdminOrganizations: async (params: ListAdminOrganizationsParams = {}): Promise<AdminOrganizationListResponse> => {
    const qs = new URLSearchParams();
    if (params.q) qs.append('q', params.q);
    if (params.sortBy) qs.append('sortBy', params.sortBy);
    if (params.sortDir) qs.append('sortDir', params.sortDir);
    if (params.page) qs.append('page', String(params.page));
    if (params.pageSize) qs.append('pageSize', String(params.pageSize));

    const response = await apiFetchWithRetry(`/v1/dashboard/admins/organizations?${qs.toString()}`);
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.message || 'Failed to load organizations');
    }
    return data as AdminOrganizationListResponse;
  },

  /**
   * Drill-down: achievements attributed to one organization (exact match).
   * `verifiedOnly` powers the "verified" cell modal; omit it for the
   * "achievements" cell. Reuses the admin-achievement item shape.
   */
  listOrganizationAchievements: async (params: {
    organization: string;
    verifiedOnly?: boolean;
    page?: number;
    pageSize?: number;
    sortBy?: AdminAchievementSortBy;
    sortDir?: 'asc' | 'desc';
  }): Promise<AdminAchievementListResponse> => {
    const qs = new URLSearchParams();
    qs.append('organization', params.organization);
    if (params.verifiedOnly) qs.append('verifiedOnly', 'true');
    if (params.sortBy) qs.append('sortBy', params.sortBy);
    if (params.sortDir) qs.append('sortDir', params.sortDir);
    if (params.page) qs.append('page', String(params.page));
    if (params.pageSize) qs.append('pageSize', String(params.pageSize));

    const response = await apiFetchWithRetry(`/v1/dashboard/admins/organizations/achievements?${qs.toString()}`);
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.message || 'Failed to load achievements');
    }
    return data as AdminAchievementListResponse;
  },

  /** Drill-down: distinct students with an achievement from one organization. */
  listOrganizationStudents: async (params: {
    organization: string;
    page?: number;
    pageSize?: number;
  }): Promise<OrganizationStudentListResponse> => {
    const qs = new URLSearchParams();
    qs.append('organization', params.organization);
    if (params.page) qs.append('page', String(params.page));
    if (params.pageSize) qs.append('pageSize', String(params.pageSize));

    const response = await apiFetchWithRetry(`/v1/dashboard/admins/organizations/students?${qs.toString()}`);
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.message || 'Failed to load students');
    }
    return data as OrganizationStudentListResponse;
  },
};
